import { O, flow, RR, s } from '@packages/prelude'
import { css, DefaultTheme, FlattenInterpolation, FlattenSimpleInterpolation, ThemedStyledProps } from 'styled-components'

// eslint-disable-next-line @typescript-eslint/sort-type-union-intersection-members
export type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'

type Style<P> = FlattenInterpolation<ThemedStyledProps<P, DefaultTheme>> | FlattenSimpleInterpolation

type StylesMap<K extends string, P> = {
  [B in K]?: Style<P>
}

const breakpoints: Record<Breakpoint, number> = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400
}

const upper: Record<Breakpoint, number | false> = {
  xs: breakpoints.sm,
  sm: breakpoints.md,
  md: breakpoints.lg,
  lg: breakpoints.xl,
  xl: breakpoints.xxl,
  xxl: false
}

export const is: (bound: Breakpoint) => <P extends object>(styles: Style<P>) => Style<P> = bound => styles =>
  upper[bound] !== undefined
    ? css`
        @media (min-width: ${breakpoints[bound]}px) and (max-width: ${upper[bound]}px) {
          ${styles}
        }
      `
    : larger(bound)(styles)

export const larger: (bound: Breakpoint) => <P extends object>(styles: Style<P>) => Style<P> = bound => styles =>
  css`
    @media (min-width: ${breakpoints[bound]}px) {
      ${styles}
    }
  `
export const smaller: (bound: Breakpoint) => <P extends object>(styles: Style<P>) => Style<P> = bound => styles =>
  css`
    @media (max-width: ${breakpoints[bound]}px) {
      ${styles}
    }
  `
export const between: (lowerBound: Breakpoint, upperBound: Breakpoint) => <P extends object>(styles: Style<P>) => Style<P> =
  (lowerBound, upperBound) => styles =>
    css`
      @media (min-width: ${breakpoints[lowerBound]}px) and (max-width: ${breakpoints[upperBound] - 0.01}px) {
        ${styles}
      }
    `

const isBreakpoint = (b: string): b is Breakpoint => RR.has(b, breakpoints)

export const match: <P extends object>(map: StylesMap<Breakpoint | '_', P>) => Style<P> = flow(
  RR.filterMap(O.fromNullable),
  RR.collect(s.Ord)((breakpoint, style) => (isBreakpoint(breakpoint) ? is(breakpoint)(style) : style))
)

export const Mobile = smaller('md')

export const Desktop = larger('md')
